"use client";
import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import primaryLogo from '../../assets/primaryLogo.svg';
import primaryLogoMobile from '../../assets/primaryLogoMobile.svg';
import { Button } from "reactstrap";
import { useOutsideClickHandler } from "../../hooks/useOutsideClickHandler";

function Header() {
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [showSolutionsDropdown, setShowSolutionsDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("");
  const solutionsMenuRef = useRef(null);

  useEffect(() => {
    setActiveTab(location.pathname);
  }, [location]);

  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  const toggleSolutionsDropdown = () => {
    setShowSolutionsDropdown(!showSolutionsDropdown);
  };

  useOutsideClickHandler(
    solutionsMenuRef,
    () => setShowSolutionsDropdown(false)
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 640) setDrawerOpen(false);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);


  useEffect(() => {
    setDrawerOpen(prev => {
      if (prev) return false;
      return prev;
    });
    setShowSolutionsDropdown(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutsideDrawer = (event) => {
      const drawerButton = document.getElementById('drawer-test');
      const navMenu = document.getElementById('nav-menu');
  
      if (
        isDrawerOpen &&
        drawerButton &&
        navMenu &&
        !drawerButton.contains(event.target) &&
        !navMenu.contains(event.target)
      ) {
        setDrawerOpen(false);
      }
    };
  
    document.addEventListener("mousedown", handleClickOutsideDrawer);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDrawer);
    };
  }, [isDrawerOpen]);
  

  return (
    <header className="sticky top-0 md:top-[52px] left-0 w-full z-50">
      <nav className="bg-[#FFFFFF] px-6 lg:px-2 max-sm:py-4 sm:py-6 lg:pt-3 lg:pb-1">
        <div className="flex flex-wrap justify-between items-center mx-auto max-w-1200">
          <Link to="/" className="flex items-center">
            <img
              src={primaryLogo}
              alt="Logo"
              width={222}
              height={34}
              className="hidden md:block"
            />
            <img
              src={primaryLogoMobile}
              alt="Logo"
              width={129}
              height={34}
              className="md:hidden"
            />
          </Link>

          <button
            id='drawer-test'
            onClick={toggleDrawer}
            type="button"
            className="inline-flex items-center p-2 ml-1 text-sm text-gray-500 rounded-lg lg:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <span className="sr-only">Open main menu</span>
            <svg
              className="w-6 h-6"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                clipRule="evenodd"
              ></path>
            </svg>
          </button>

          <div
            id="nav-menu"
            className={`${
            isDrawerOpen
              ? "custom-drawer block absolute shadow-lg top-16 left-0 z-50 bg-white"
              : "hidden"
            } justify-between items-center w-full lg:flex lg:w-auto`}
          >
            <ul
              className="flex flex-col mt-4 font-medium text-center lg:flex-row lg:space-x-2 lg:mt-0"
              style={{ fontFamily: '"Inter", sans-serif' }}
            >
              <li className="relative solutions-menu" ref={solutionsMenuRef}>
                <button
                  onClick={toggleSolutionsDropdown}
                  className={`flex items-center justify-center gap-2 px-3 py-2 font-sans text-sm w-full text-left
                    ${activeTab.includes("/solutions") ? "text-darkPurple font-bold" : "text-darkGray font-semibold hover:text-darkPurple"}`}
                >
                  Solutions
                  <svg
                    className={`w-3 h-2 transition-transform duration-200 
                      ${showSolutionsDropdown ? 'rotate-180' : ''} 
                      group-hover:fill-primary fill-darkGray`}
                    viewBox="0 0 12 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0.813195 0.646447C1.00846 0.451184 1.32504 0.451184 1.5203 0.646447L6.00008 5.12623L10.4799 0.646447C10.6751 0.451184 10.9917 0.451184 11.187 0.646447C11.3822 0.841709 11.3822 1.15829 11.187 1.35355L6.35363 6.18689C6.15837 6.38215 5.84179 6.38215 5.64653 6.18689L0.813195 1.35355C0.617933 1.15829 0.617933 0.841709 0.813195 0.646447Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                {showSolutionsDropdown && (
                  <div className={`${!isDrawerOpen && 'absolute top-full left-0 shadow-lg rounded-md'} bg-white py-2 z-50 lg:w-64`}>
                    <Link
                      to="/solutions/corporate-management"
                      className={`block px-4 py-2 text-sm hover:bg-gray-100
                        ${activeTab === "/solutions/corporate-management" ? "text-darkPurple font-bold" : "text-darkGray font-semibold hover:text-darkPurple"}`}
                    >
                      Corporate Management
                    </Link>
                    <Link
                      to="/solutions/license-management"
                      className={`block px-4 py-2 text-sm hover:bg-gray-100
                        ${activeTab === "/solutions/license-management" ? "text-darkPurple font-bold" : "text-darkGray font-semibold hover:text-darkPurple"}`}
                    >
                      License Management
                    </Link>
                    <Link
                      to="/solutions/compliance-assurance"
                      className={`block px-4 py-2 text-sm hover:bg-gray-100
                        ${activeTab === "/solutions/compliance-assurance" ? "text-darkPurple font-bold" : "text-darkGray font-semibold hover:text-darkPurple"}`}
                    >
                      Compliance Assurance
                    </Link>
                    <Link
                      to="/solutions/consultant-advisors"
                      className={`block px-4 py-2 text-sm hover:bg-gray-100
                        ${activeTab === "/solutions/consultant-advisors" ? "text-darkPurple font-bold" : "text-darkGray font-semibold hover:text-darkPurple"}`}
                    >
                      Consultants & Advisors
                    </Link>
                  </div>
                )}
              </li>
              <li>
                <Link
                  to="/security"
                  className={`block px-3 py-2 font-sans text-sm ${activeTab === "/security" ? "text-darkPurple font-bold" : "text-darkGray font-semibold hover:text-darkPurple"}`}>
                  Security
                </Link>
              </li>
              <li>
                <Link
                  to="/about-us"
                  className={`block px-3 py-2 font-sans text-sm ${activeTab === "/about-us" ? "text-darkPurple font-bold" : "text-darkGray font-semibold hover:text-darkPurple"}`}>
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contact-us"
                  className={`block px-3 py-2 font-sans text-sm ${activeTab === "/contact-us" ? "text-darkPurple font-bold" : "text-darkGray font-semibold hover:text-darkPurple"}`}>
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className={`block px-3 py-2 font-sans text-sm ${activeTab === "/blog" ? "text-darkPurple font-bold" : "text-darkGray font-semibold hover:text-darkPurple"}`}>
                  Blog
                </Link>
              </li>
            </ul>
            <div
              className={`${
                isDrawerOpen ? "flex " : "hidden"
              } px-2 mb-6 flex-col justify-center items-center gap-2 relative`}
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              <Button 
              onClick={()=> navigate("/book-demo")}
                className="px-5 py-2 w-full text-center text-white text-sm leading-6 font-semibold font-sans rounded-3xl border bg-[#510AC9]" 
              >
                Demo
              </Button>
            
              <Button disabled className="px-5 py-2 w-full text-center text-primary hover:text-white hover:bg-darkPurple text-sm leading-6 font-semibold font-sans rounded-3xl border border-lightBlue disabled:opacity-50">
              {/* <a
                href="https://app-dev.regtechflow.net/login"
                
                target="_blank"
                rel="noopener noreferrer"
              > */}
                Login
              {/* </a> */}
              </Button>
              <div className="text-center text-darkGray font-bold text-sm">
                Available only in desktop
              </div>    
            </div>
          </div>

          <div
            id="nav-button"
            className={`relative lg:flex hidden justify-center items-center gap-2 `}
            style={{ fontFamily: '"Open Sans", sans-serif' }}
          >
            <a
              href="https://app.regtechflow.com/login"
              target="_black"
              className="font-sans px-5 py-1 min-w-[85px] text-center hover:text-white hover:bg-darkPurple text-primary text-sm leading-6 font-semibold rounded-3xl border border-lightBlue max-lg:pointer-events-none"
            >
              Login
            </a>

            <Button 
              className="font-sans px-5 py-1 min-w-[85px] text-center text-white text-sm leading-6 font-semibold rounded-3xl border bg-[#510AC9]"
              onClick={()=> navigate("/book-demo")}
            >
              Demo
            </Button>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Header;